import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { ApiError } from 'api/core/ApiError';
import { DailyNavOverrideForm } from 'api/models/DailyNavOverrideForm';
import { DailyNavService } from 'api/services/DailyNavService';
import {
  DAILY_NAV_OVERRIDE_FORM_QUERY_KEY,
  DAILY_NAV_RECORDS_BY_FIRM_QUERY_KEY,
} from 'common/constants/services/firms';
import { OverrideDailyNavPriceParams } from './useDailyNavPriceOverride.types';

// The server's `detail` when it sent one: override refusals are worded for the user.
const errorDetail = (error: unknown, fallback: string): string => {
  if (error instanceof ApiError && typeof error.body === 'object' && error.body) {
    const { detail } = error.body as Record<string, unknown>;
    if (typeof detail === 'string') {
      return detail;
    }
  }
  return fallback;
};

const useDailyNavOverrideForm = (recordId: number | undefined, enabled: boolean) =>
  useQuery<DailyNavOverrideForm, Error>({
    enabled: enabled && recordId != null,
    queryKey: [DAILY_NAV_OVERRIDE_FORM_QUERY_KEY, recordId],
    queryFn: async () => {
      try {
        return await DailyNavService.apiDailyNavOverrideList(String(recordId));
      } catch (error) {
        throw new Error(errorDetail(error, 'Could not load the current prices.'));
      }
    },
    // Fetched fresh each time the dialog opens (enabled flips on), and only then:
    // a focus refetch mid-edit would swap the inputs out from under the user.
    meta: { persist: false },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

const useOverrideDailyNavPrice = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ recordId, data }: OverrideDailyNavPriceParams) =>
      DailyNavService.apiDailyNavOverrideCreate(String(recordId), data),
    onSuccess: result => {
      queryClient.invalidateQueries({ queryKey: [DAILY_NAV_RECORDS_BY_FIRM_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [DAILY_NAV_OVERRIDE_FORM_QUERY_KEY] });
      enqueueSnackbar(`Price overridden (valuation version ${result.version}).`, { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(errorDetail(error, 'Failed to override the price.'), { variant: 'error' });
    },
  });
};

export { useDailyNavOverrideForm, useOverrideDailyNavPrice };
